/**
 * Paying for Pro without connecting a wallet: the app shows a Solana Pay address and QR for the
 * exact sum, the person pays from any wallet, and the server finds the transfer by its
 * reference (a one-time key the wallet puts into the transaction) and credits the account.
 * Shared by the page and the server function (netlify/functions/api/pay.ts); nothing here
 * touches the network.
 */

/** USDC on Solana mainnet */
export const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
/** A payment request is good for this long; a transfer that lands later is still credited when checked */
export const INTENT_MS = 60 * 60 * 1000;
/** How often the page asks whether the transfer has landed */
export const CHECK_EVERY_MS = 6000;

export interface PaymentRequest {
    /** The one-time key the wallet includes in the transfer, so the server can find it */
    reference: string;
    /** Where to pay: the site's USDC wallet */
    recipient: string;
    /** USDC to pay */
    amount: number;
    /** Months of Pro this buys */
    credits: number;
    /** The Solana Pay address, for a QR or a wallet app */
    url: string;
    expiresAt: number;
}

export type PaymentState =
    | { status: 'pending' }
    | { status: 'paid'; signature: string; newCredits: number }
    | { status: 'expired' };

/** A USDC sum as the URL carries it: a plain decimal, never an exponent, at most six places */
export const amountText = (amount: number): string => {
    const fixed = amount.toFixed(6);
    return fixed.replace(/\.?0+$/, '') || '0';
};

/** A Solana Pay transfer request (https://docs.solanapay.com/spec#transfer-request) for USDC */
export const solanaPayUrl = (input: { recipient: string; amount: number; reference: string; label?: string; message?: string; memo?: string }): string => {
    const params = new URLSearchParams();
    params.set('amount', amountText(input.amount));
    params.set('spl-token', USDC_MINT);
    params.set('reference', input.reference);
    if (input.label) params.set('label', input.label);
    if (input.message) params.set('message', input.message);
    if (input.memo) params.set('memo', input.memo);
    // Spaces as %20, as wallets expect, not as +
    return `solana:${input.recipient}?${params.toString().replace(/\+/g, '%20')}`;
};

/** A base58 Solana address, by shape */
export const looksLikeAddress = (value: unknown): value is string => typeof value === 'string' && /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value);
